import React, { useRef, useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
// Import Shared Components
import { SubmitButton } from 'shared-components';
// Import Global Components
import Label from '../../../../components/Label';
import Legend from '../../../../components/Legend';
// Import Component Styles
import {
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputContainer,
  CardCaptureFrame,
} from './styles';
// Import Translations
import { redeemEn } from '../../../../i18n/redeem';

// How long we wait for the capture iframe to answer a submit request
const CAPTURE_RESPONSE_TIMEOUT_MS = 15000;

const frameOrigin = url => new URL(url, window.location.origin).origin;

// **TODO We should be using the shared components for inputs and labels**
export default injectIntl(
  ({
    intl,
    handleFormSubmission,
    collectionAmount,
    cardCaptureIframeUrl,
    setError,
    refreshCaptureFrame,
  }) => {
    const firstNameInput = useRef(null);
    const lastNameInput = useRef(null);
    const postalCodeInput = useRef(null);
    const cardNumberInput = useRef(null);
    const expInput = useRef(null);
    const cvvInput = useRef(null);
    const amountInput = useRef(null);
    // Iframe mode: the hosted capture iframe posts back a card token + last four
    const captureFrame = useRef(null);
    // Parent fields captured at submit time while we wait on the iframe
    const pendingSubmit = useRef(null);
    const submitRef = useRef(null);
    const refreshRef = useRef(null);
    const [frameHeight, setFrameHeight] = useState(165);
    // Submit disabled while a tokenize request is out to the capture iframe
    const [submitting, setSubmitting] = useState(false);

    submitRef.current = handleFormSubmission;
    refreshRef.current = refreshCaptureFrame;

    useEffect(() => {
      if (!cardCaptureIframeUrl) return undefined;

      const iframeOrigin = frameOrigin(cardCaptureIframeUrl);
      const handleMessage = event => {
        if (event.origin !== iframeOrigin) return;
        const message = event.data || {};

        // The iframe broadcasts its content height
        if (typeof message.height === 'number') {
          setFrameHeight(Math.min(Math.max(message.height, 150), 320));
          return;
        }

        const payload = message.data || {};
        if (payload.token) {
          setSubmitting(false);
          if (pendingSubmit.current) {
            clearTimeout(pendingSubmit.current.timeoutId);
            const { fields } = pendingSubmit.current;
            pendingSubmit.current = null;
            submitRef.current({
              ...fields,
              token: payload.token,
              lastFour: payload.last_four ? String(payload.last_four) : null,
              postalCode: message.postal_code || null,
            });
          }
          return;
        }

        if (message.type === 'card_capture_validation_error' || message.error) {
          setSubmitting(false);
          if (pendingSubmit.current) {
            clearTimeout(pendingSubmit.current.timeoutId);
            pendingSubmit.current = null;
          }
          setError(
            message.type === 'card_capture_validation_error'
              ? message.errors.join(' ')
              : redeemEn['redeem-form-error-alert']
          );
          // A tokenize failure burns the frame's single-use session; mint a
          // fresh frame so the payer can retry (validation errors don't)
          if (message.error && refreshRef.current) {
            refreshRef.current();
          }
        }
      };

      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
        if (pendingSubmit.current) {
          clearTimeout(pendingSubmit.current.timeoutId);
          pendingSubmit.current = null;
        }
      };
    }, [cardCaptureIframeUrl, setError]);

    const handleOnSubmit = e => {
      e.preventDefault();

      const parentFields = {
        firstName: firstNameInput.current.value,
        lastName: lastNameInput.current.value,
        amount: amountInput.current.value,
      };

      if (cardCaptureIframeUrl) {
        // A submit is already in flight to the iframe
        if (pendingSubmit.current) return;

        // Ask the iframe to validate and tokenize its card fields, then
        // finish the submission when the token message arrives
        setError(null);
        setSubmitting(true);
        const timeoutId = setTimeout(() => {
          pendingSubmit.current = null;
          setSubmitting(false);
          setError(redeemEn['redeem-form-error-alert']);
          // The frame is unresponsive; replace it with a fresh session
          if (refreshRef.current) refreshRef.current();
        }, CAPTURE_RESPONSE_TIMEOUT_MS);
        pendingSubmit.current = { fields: parentFields, timeoutId };
        captureFrame.current.contentWindow.postMessage(
          {
            type: 'card_capture_submit',
            first_name: parentFields.firstName,
            last_name: parentFields.lastName,
          },
          frameOrigin(cardCaptureIframeUrl)
        );
        return;
      }

      handleFormSubmission({
        cardNumber: cardNumberInput.current.value,
        exp: expInput.current.value,
        cvv: cvvInput.current.value,
        postalCode: postalCodeInput.current.value,
        ...parentFields,
      });
    };

    return (
      <Form id="redeem" method="post" onSubmit={handleOnSubmit}>
        <FormGroup form="redeem">
          <Legend
            legend={redeemEn['redeem-cardInformation-legend']}
            legendFor="cardInformation"
            page="redeem"
          />
          <InputGroup>
            <InputContainer>
              <Label
                label={redeemEn['redeem-firstName-label']}
                labelFor="firstName"
                page="redeem"
              />
              <Input name="firstName" required ref={firstNameInput} />
            </InputContainer>
            <InputContainer>
              <Label
                label={redeemEn['redeem-lastName-label']}
                labelFor="lastName"
                page="redeem"
              />
              <Input name="lastName" required ref={lastNameInput} />
            </InputContainer>
          </InputGroup>
          <InputGroup>
            <InputContainer>
              <Label
                label={redeemEn['redeem-amount-label']}
                labelFor="amount"
                page="redeem"
              />
              <Input
                name="amount"
                type="number"
                min="0"
                max={collectionAmount}
                title="Please enter an amount less that or equal to the full Payment Amount"
                step="0.01"
                required
                ref={amountInput}
              />
            </InputContainer>
          </InputGroup>
          {cardCaptureIframeUrl ? (
            /* Card data (and postal code) is entered in the hosted capture iframe */
            <CardCaptureFrame
              src={cardCaptureIframeUrl}
              title={redeemEn['redeem-cardInformation-legend']}
              sandbox="allow-scripts allow-same-origin allow-forms"
              style={{ height: `${frameHeight}px` }}
              ref={captureFrame}
            />
          ) : (
            <>
              <InputGroup>
                <InputContainer>
                  <Label
                    label={redeemEn['redeem-cardNumber-label']}
                    labelFor="cardNumber"
                    page="redeem"
                  />
                  <Input
                    name="cardNumber"
                    pattern="[0-9]+"
                    title="Please enter digits only"
                    required
                    ref={cardNumberInput}
                  />
                </InputContainer>
              </InputGroup>
              <InputGroup>
                <InputContainer>
                  <Label
                    label={redeemEn['redeem-cvv-label']}
                    labelFor="cvv"
                    page="redeem"
                  />
                  <Input
                    name="cvv"
                    required
                    pattern="[0-9]{3}"
                    title="Please enter 3 Digit CVV"
                    ref={cvvInput}
                  />
                </InputContainer>
                <InputContainer>
                  <Label
                    label={redeemEn['redeem-exp-label']}
                    labelFor="exp"
                    page="redeem"
                  />
                  <Input
                    name="exp"
                    pattern="[0-9]{4}"
                    title="Please enter 4 Digits"
                    required
                    ref={expInput}
                  />
                </InputContainer>
                <InputContainer>
                  <Label
                    label={redeemEn['redeem-postalCode-label']}
                    labelFor="postalCode"
                    page="redeem"
                  />
                  <Input name="postalCode" required ref={postalCodeInput} />
                </InputContainer>
              </InputGroup>
            </>
          )}
        </FormGroup>
        <SubmitButton
          buttonType="primary"
          size="large"
          disabled={submitting}
          value={intl.formatMessage({
            id: 'redeem-submit-button',
            defaultMessage: redeemEn['redeem-submit-button'],
          })}
        />
      </Form>
    );
  }
);
